// Chrome-less embeddable widget: a single-MLS slice of the Gitcha feed for MLS
// partner iframes. Tenant/MLS gating is server-side by widgetKey.
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { PropertyListing } from "../../components";
import useWidgetConfig from "../../hooks/useWidgetConfig";
import { useImpressionTracker } from "../../hooks/useImpressionTracker";
import { getEmbedParentOrigin } from "../../helpers/reachViewer";
import useWidgetWantAds from "../../hooks/useWidgetWantAds";
import useWidgetNotifications from "../../hooks/useWidgetNotifications";
import { applyEmbedTheme } from "../../helpers/embedTheme";
import WidgetSinceLastVisit, {
  WidgetSeenDivider,
} from "../../components/WidgetSinceLastVisit";
import {
  readLastVisit,
  writeLastVisit,
  newSinceLastVisit,
} from "../../helpers/widgetLastVisit";
import { tenantOrigin } from "../../helpers/embedTenantOrigin";
import {
  loadSeenIds,
  saveSeenIds,
  countUnseen,
  planBellOpen,
  buildNotificationUrl,
  areWidgetNotificationsEnabled,
} from "../../helpers/widgetNotifications";
import { recordWidgetNotificationEvent } from "../../services/v2/widgetService";
import bellIcon from "../../images/bell.svg";
import "./IframeWidget.scss";

// The widget has no sort UI — it always shows the most recent want-ads.
const SORT_TYPE = "newest";

const IframeWidget = () => {
  const { widgetKey } = useParams();
  const trackCardImpression = useImpressionTracker({
    surface: "mls_widget",
    requireScroll: true,
    payload: { widgetKey, parentOrigin: getEmbedParentOrigin() },
  });

  const {
    data: config,
    isLoading: isConfigLoading,
    isError: isConfigError,
  } = useWidgetConfig(widgetKey);

  // Stays off until the tenant config has loaded, so a switched-off widget never
  // flashes a badge or calls the notifications API.
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);

  // applyEmbedTheme pushes the tenant's themeVars (colors) onto the document so
  // the widget brands itself from the tenant config - no hardcoding.
  useEffect(() => {
    if (!config?.tenant_slug) return;
    applyEmbedTheme(config.tenant_slug).then((theme) =>
      setNotificationsEnabled(areWidgetNotificationsEnabled(theme)),
    );
  }, [config]);

  const {
    data: feed,
    isLoading: isFeedLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useWidgetWantAds({ widgetKey, sortType: SORT_TYPE, enabled: !!config });

  const ads = feed?.ads ?? [];
  const totalCount = feed?.totalCount ?? 0;

  const [lastVisit] = useState(() => readLastVisit(widgetKey));

  useEffect(() => {
    if (!isFeedLoading && ads.length > 0) writeLastVisit(widgetKey);
  }, [isFeedLoading, ads.length, widgetKey]);

  const newSince = useMemo(
    () => newSinceLastVisit(ads, lastVisit),
    [ads, lastVisit],
  );

  const seenDividerAt = useMemo(() => {
    if (!newSince || newSince.atLeast) return -1;
    const firstSeen = ads.findIndex(
      (ad) => !newSince.ids.includes(ad.Want_ad_Id),
    );
    return firstSeen === newSince.count ? firstSeen : -1;
  }, [ads, newSince]);

  const marketName = [config?.market?.city, config?.market?.state]
    .filter(Boolean)
    .join(", ");

  // Admin-authored broadcasts. Viewers are anonymous, so "seen" lives in this
  // browser only; opening the bell marks everything currently listed as seen.
  const { data: fetchedNotifications = [] } = useWidgetNotifications(
    widgetKey,
    !!config && notificationsEnabled,
  );
  const notifications = notificationsEnabled ? fetchedNotifications : [];
  const [seenIds, setSeenIds] = useState(() => loadSeenIds(widgetKey));
  const [bellOpen, setBellOpen] = useState(false);
  const bellBarRef = useRef(null);
  const unseenCount = countUnseen(notifications, seenIds);

  // Outbound-only: open the want-ad on the MLS's own environment in a new tab.
  const openAd = (adSlug) => {
    if (adSlug && config?.tenant_slug)
      window.open(
        `${tenantOrigin(config.tenant_slug)}/want-listing-details/${adSlug}?utm_source=mls_widget&utm_medium=widget`,
        "_blank",
        "noopener",
      );
  };
  const handleItemClick = (_id, slug) => openAd(slug);
  const handleFavorite = (_isFav, wantAdId) =>
    openAd(ads.find((a) => a.Want_ad_Id === wantAdId)?.slug);

  // CTAs → the tenant's BLS environment (its root shows sign-up/sign-in).
  const openTenant = (destination = "/home") => {
    if (!config?.tenant_slug) return;
    window.open(
      `${tenantOrigin(config.tenant_slug)}/?redirect=${encodeURIComponent(destination)}`,
      "_blank",
      "noopener",
    );
  };
  const openWhatIsNew = () => {
    if (newSince?.onlySlug) {
      openAd(newSince.onlySlug);
      return;
    }
    openTenant("/home");
  };

  // With no broadcasts the bell keeps its original behavior (open the tenant).
  const handleBellClick = () => {
    if (notifications.length === 0) return openTenant();
    if (bellOpen) return setBellOpen(false);
    const { unseen, seen } = planBellOpen(notifications, seenIds);
    recordWidgetNotificationEvent(widgetKey, "view", unseen);
    setSeenIds(seen);
    saveSeenIds(widgetKey, seen);
    setBellOpen(true);
  };

  // The dropdown covers the cards in a small iframe, so it must be dismissable
  // without re-aiming at the bell: click anywhere outside it, or press Escape.
  useEffect(() => {
    if (!bellOpen) return undefined;
    const onPointerDown = (e) => {
      if (!bellBarRef.current?.contains(e.target)) setBellOpen(false);
    };
    const onKeyDown = (e) => {
      if (e.key === "Escape") setBellOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [bellOpen]);

  const openNotification = (notification) => {
    const url = buildNotificationUrl(
      config?.tenant_slug && tenantOrigin(config.tenant_slug),
      notification,
    );
    // Only count a click that actually navigated.
    if (url) {
      window.open(url, "_blank", "noopener");
      recordWidgetNotificationEvent(widgetKey, "click", [notification.id]);
    }
    setBellOpen(false);
  };

  if (isConfigLoading) {
    return (
      <div className="gitcha-embed-widget">
        <div className="gew-loading">Loading…</div>
      </div>
    );
  }

  if (isConfigError || !config) {
    return (
      <div className="gitcha-embed-widget">
        <div className="gew-state">This widget is unavailable.</div>
      </div>
    );
  }

  return (
    <div className="gitcha-embed-widget">
      <div className={`gew-headrow${newSince ? " gew-headrow--new" : ""}`}>
        <WidgetSinceLastVisit
          newSince={newSince}
          totalCount={totalCount}
          marketName={marketName}
          onOpenNew={openWhatIsNew}
          onOpenFeed={() => openTenant("/home")}
        />
        <div className="gew-bellbar" ref={bellBarRef}>
          <button
            type="button"
            className="gew-bell"
            onClick={handleBellClick}
            aria-label={
              unseenCount > 0
                ? `Notifications, ${unseenCount} new`
                : "Notifications"
            }
            aria-expanded={bellOpen}
          >
            <img src={bellIcon} alt="" />
            {unseenCount > 0 && (
              <span className="gew-bell-badge">
                {unseenCount > 9 ? "9+" : unseenCount}
              </span>
            )}
          </button>
          {bellOpen && (
            <div className="gew-notifications">
              <div className="gew-notifications-header">Notifications</div>
              {notifications.map((n) => (
                <button
                  key={n.id}
                  type="button"
                  className="gew-notification"
                  onClick={() => openNotification(n)}
                >
                  <span className="gew-notification-icon" aria-hidden="true">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M3 11v3a1 1 0 0 0 1 1h2l5 4V6L6 10H4a1 1 0 0 0-1 1z" />
                      <path d="M15.5 8.5a5 5 0 0 1 0 7" />
                      <path d="M18.5 5.5a9 9 0 0 1 0 13" />
                    </svg>
                  </span>
                  <span className="gew-notification-text">
                    <span className="gew-notification-title">{n.title}</span>
                    <span className="gew-notification-body">{n.body}</span>
                    {n.ctaLabel && (
                      <span className="gew-notification-cta">{n.ctaLabel}</span>
                    )}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="gew-list">
        {ads.length === 0 && !isFeedLoading ? (
          <div className="gew-state">No buyer want-listings right now.</div>
        ) : (
          ads.map((ad, index) => (
            <React.Fragment key={ad.Want_ad_Id}>
              {index === seenDividerAt && <WidgetSeenDivider />}
              <PropertyListing
                list={ad}
                impressionRef={trackCardImpression(ad.Want_ad_Id)}
                embedMode
                embedShareBaseUrl={tenantOrigin(config.tenant_slug)}
                handleWantAdItemClick={handleItemClick}
                handleFavoriteOrUnfavoriteWantAd={handleFavorite}
                onChangeText={() => {}}
                onAddClient={() => {}}
              />
            </React.Fragment>
          ))
        )}
        {(isFeedLoading || isFetchingNextPage) && (
          <div className="gew-loading">Loading…</div>
        )}
      </div>

      {hasNextPage && (
        <div className="gew-footer">
          <button
            type="button"
            className="gew-showmore"
            onClick={() => fetchNextPage()}
            disabled={isFetchingNextPage}
          >
            View all {totalCount} buyers
          </button>
        </div>
      )}
    </div>
  );
};

export default IframeWidget;
